import type { ReactNode } from 'react';
import { LanguageSwitcher } from '../i18n/LanguageSwitcher';

/**
 * The shared frame for the pre-auth pages (P-01…P-06).
 *
 * Masthead, eyebrow, title, then the form on the ground — no card. Keeping it
 * in one place is what stops Sign In and Apply drifting apart, which is exactly
 * what happened to the two prototypes earlier in this project.
 */
export function AuthPage(
  { eyebrow, title, lede, children, wide }:
  { eyebrow?: string; title: string; lede?: ReactNode; children: ReactNode; wide?: boolean },
) {
  return (
    <div className="mk-auth">
      <div className="mk-auth-col" style={wide ? { maxWidth: 600 } : undefined}>
        {/* Brand, not copy: the wordmark and its motto are the same in every
            language, and translating a name is how you lose it. */}
        {/* eslint-disable no-restricted-syntax */}
        <div className="mk-auth-mark">
          <b>MIRAKI</b>
          <i>MIRACULOUS · RARE · KINDRED</i>
        </div>
        {/* eslint-enable no-restricted-syntax */}
        {/* D1 makes this one front door for staff and customers alike, so the
            switcher sits here rather than on a customer-only shell. Whoever is
            signing in, the page they meet first is in their language. */}
        <div className="mk-auth-lang"><LanguageSwitcher /></div>
        {eyebrow ? <p className="mk-auth-eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {lede ? <p className="mk-auth-lede">{lede}</p> : null}
        {children}
      </div>
    </div>
  );
}

export function Label(
  { htmlFor, required, children }: { htmlFor: string; required?: boolean; children: ReactNode },
) {
  return (
    <>
      <label htmlFor={htmlFor}>{children}</label>
      {/*
        Deliberately a SIBLING of the label, not a child.
        Inside it, the field's accessible name becomes "Email address*" — a
        screen reader reads the asterisk as part of the name. The marker is
        decoration; `required` on the input is what actually announces the
        obligation, so the asterisk must not be the only signal nor part of
        the name.
      */}
      {required ? <span className="mk-auth-req" aria-hidden>*</span> : null}
    </>
  );
}

/** Inline so there is no icon dependency and no request for one glyph. */
export function Eye({ off }: { off?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.8 12S5.5 5.2 12 5.2 22.2 12 22.2 12 18.5 18.8 12 18.8 1.8 12 1.8 12Z" />
      <circle cx="12" cy="12" r="3.1" />
      {off ? <line x1="3.5" y1="20.5" x2="20.5" y2="3.5" /> : null}
    </svg>
  );
}
